import React from 'react';
import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import EntityTerminationReviewApprovalForm from './EntityTerminationReviewApprovalForm';
import { IEntityTerminationReviewApproval } from '../types';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import { APPROVE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW } from '../../../utils/statApi/EntityTerminationApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';

interface IEntityTerminationReviewApprovalPanelProps {
  closePanel: () => void;
  data: IEntityTerminationReviewApproval;
}

const EntityTerminationReviewApprovalPanel: React.FC<
  IEntityTerminationReviewApprovalPanelProps
> = ({ closePanel, data }) => {
  const form = useForm<IEntityTerminationReviewApproval>();
  const { handleSubmit, watch } = form;

  const legoCategory = watch('legoCategory');
  const terminationLifecycleStatus = watch('terminationLifecycleStatus');

  // Track if Blocker Category is required
  const [isLegoCategoryRequired, setIsLegoCategoryRequired] = React.useState(true);

  const [approveEntityTerminationReview, { loading, error }] = useMutation(
    APPROVE_ENTITY_TERMINATION_ELIGIBILITY_REVIEW,
    {
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IEntityTerminationReviewApproval): void => {
    approveEntityTerminationReview({
      variables: {
        eligibilityReviewInputDto: {
          reviewId: data.reviewId,
          approvalId: data.review.reviewApprovals[0]?.id,
          legoCategoryId: formData.legoCategory?.id,
          terminationLifecycleStatusId: formData.terminationLifecycleStatus?.id,
        },
      },
    });
  };

  const isSaveDisabled =
    !terminationLifecycleStatus?.fieldName?.trim() ||
    (isLegoCategoryRequired ? !legoCategory?.fieldName?.trim() : false);

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Approve Request"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Approve Request"
          saveTitle="Approve Request"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
          disabled={isSaveDisabled}
        />
      )}
    >
      <form>
        <LoadingErrorMessage error={error} loading={loading} />
        <EntityTerminationReviewApprovalForm
          form={form}
          data={data}
          setIsLegoCategoryRequired={setIsLegoCategoryRequired}
        />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationReviewApprovalPanel;
